import React, { useState, useEffect, useRef, useCallback } from "react";
import { useField, useFormikContext } from "formik";
import axios from "axios";

const VillageAutocomplete = ({
  name,
  placeholder,
  disabled,
  className,
  onKeyDown,
  onSelect,
  inputRef,
  renderInput,
  enabled = false, // Default is OFF
}) => {
  const [field, meta, helpers] = useField(name);
  const { setFieldValue } = useFormikContext();
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);

  const getInputEl = useCallback(() => {
    return (
      inputRef?.current ||
      wrapperRef.current?.querySelector("input") ||
      null
    );
  }, [inputRef]);

  // If enabled changes to false, immediately clear suggestions and release suppression
  useEffect(() => {
    if (!enabled) {
      setSuggestions([]);
      setShowSuggestions(false);
      const el = getInputEl();
      if (el) {
        el.dataset.suppressTranslate = "false";
        el.classList.remove("suppress-translate");
      }
    }
  }, [enabled, getInputEl]);

  // Debounce API calls
  const fetchSuggestions = useCallback(
    async (query) => {
      if (!enabled || !query || query.trim().length === 0) {
        setSuggestions([]);
        const el = getInputEl();
        if (el) {
          el.dataset.suppressTranslate = "false";
          el.classList.remove("suppress-translate");
        }
        return;
      }
      setLoading(true);
      const el = getInputEl();
      try {
        const token =
          localStorage.getItem("dwt-cust-tok") ||
          localStorage.getItem("dwt-tok") ||
          localStorage.getItem("dwt-entry-tok") ||
          localStorage.getItem("access_token") ||
          localStorage.getItem("org_access_token") ||
          localStorage.getItem("token") ||
          "";
        const config = token
          ? { headers: { Authorization: `Bearer ${token}` } }
          : {};

        const rawBaseUrl =
          process.env.REACT_APP_API_URL ||
          process.env.REACT_APP_BACKEND_URL ||
          "http://localhost:8000";
        const baseUrl = rawBaseUrl.replace(/\/+$/, "");
        const res = await axios.get(
          `${baseUrl}/common/villages/search?q=${encodeURIComponent(
            query.trim()
          )}`,
          config
        );

        if (res.data && Array.isArray(res.data.data)) {
          const found = res.data.data;
          setSuggestions(found);
          if (found.length > 0) {
            // If there is any value to show in suggestion dropdown, do not show Tamil suggestion
            if (el) {
              el.dataset.suppressTranslate = "true";
              el.classList.add("suppress-translate");
            }
            document.dispatchEvent(
              new CustomEvent("dismiss-tamil-suggestions")
            );
          } else {
            // If nothing is in suggestion dropdown, start showing Tamil suggestion
            if (el) {
              el.dataset.suppressTranslate = "false";
              el.classList.remove("suppress-translate");
              if (/[a-zA-Z]/.test(el.value)) {
                el.dispatchEvent(
                  new CustomEvent("request-tamil-suggestion", {
                    bubbles: true,
                  })
                );
              }
            }
          }

          try {
            const cached = JSON.parse(
              localStorage.getItem("cached_villages") || "[]"
            );
            const merged = Array.from(
              new Set([...cached, ...res.data.data])
            ).slice(0, 500);
            localStorage.setItem("cached_villages", JSON.stringify(merged));
          } catch (e) {}
        }
      } catch (err) {
        console.warn(
          "Failed to fetch village suggestions, falling back to local cache:",
          err
        );
        try {
          const cached = JSON.parse(
            localStorage.getItem("cached_villages") || "[]"
          );
          const lowerQ = query.trim().toLowerCase();
          const filtered = cached.filter(
            (v) => v && v.toLowerCase().includes(lowerQ)
          );
          const found = filtered.slice(0, 20);
          setSuggestions(found);
          if (found.length > 0) {
            if (el) {
              el.dataset.suppressTranslate = "true";
              el.classList.add("suppress-translate");
            }
            document.dispatchEvent(
              new CustomEvent("dismiss-tamil-suggestions")
            );
          } else {
            if (el) {
              el.dataset.suppressTranslate = "false";
              el.classList.remove("suppress-translate");
              if (/[a-zA-Z]/.test(el.value)) {
                el.dispatchEvent(
                  new CustomEvent("request-tamil-suggestion", {
                    bubbles: true,
                  })
                );
              }
            }
          }
        } catch (e) {
          setSuggestions([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [enabled, getInputEl]
  );

  useEffect(() => {
    const val = field?.value ? String(field.value).trim() : "";
    const el = getInputEl();
    if (!enabled || !val) {
      setSuggestions([]);
      setHighlightedIndex(-1);
      if (el) {
        el.dataset.suppressTranslate = "false";
        el.classList.remove("suppress-translate");
      }
      document.dispatchEvent(new CustomEvent("dismiss-tamil-suggestions"));
      return;
    }

    const delayDebounceFn = setTimeout(() => {
      fetchSuggestions(val);
    }, 150);

    return () => clearTimeout(delayDebounceFn);
  }, [field.value, fetchSuggestions, getInputEl, enabled]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [suggestions]);

  // Ensure transliteration is suppressed whenever village suggestions are visible
  useEffect(() => {
    const el = getInputEl();
    if (!el) return;
    if (enabled && showSuggestions && suggestions.length > 0) {
      el.dataset.suppressTranslate = "true";
      el.classList.add("suppress-translate");
      document.dispatchEvent(new CustomEvent("dismiss-tamil-suggestions"));
    } else if (!enabled || suggestions.length === 0) {
      el.dataset.suppressTranslate = "false";
      el.classList.remove("suppress-translate");
    }
  }, [enabled, showSuggestions, suggestions, getInputEl]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (village) => {
    helpers.setValue(village);
    setShowSuggestions(false);
    setSuggestions([]);
    setHighlightedIndex(-1);
    const el = getInputEl();
    if (el) {
      el.dataset.suppressTranslate = "false";
      el.classList.remove("suppress-translate");
    }
    document.dispatchEvent(new CustomEvent("dismiss-tamil-suggestions"));
    const th = document.getElementById("tamil-typing-helper");
    if (th) th.remove();
    const hh = document.getElementById("tamil-history-helper");
    if (hh) hh.remove();
    try {
      const cached = JSON.parse(
        localStorage.getItem("cached_villages") || "[]"
      );
      if (!cached.includes(village)) {
        cached.unshift(village);
        localStorage.setItem(
          "cached_villages",
          JSON.stringify(cached.slice(0, 500))
        );
      }
    } catch (e) {}
    if (onSelect) {
      onSelect(village);
    }
  };

  const handleKeyDownInternal = (e) => {
    if (enabled && showSuggestions && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev + 1 >= suggestions.length ? 0 : prev + 1
        );
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev <= 0 ? suggestions.length - 1 : prev - 1
        );
        return;
      }
      if (
        e.key === "Enter" &&
        highlightedIndex >= 0 &&
        suggestions[highlightedIndex]
      ) {
        e.preventDefault();
        e.stopPropagation();
        handleSelect(suggestions[highlightedIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setShowSuggestions(false);
        return;
      }
    }
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  return (
    <div
      className="relative w-full village-autocomplete"
      ref={wrapperRef}
      onFocusCapture={() => {
        if (enabled) {
          setShowSuggestions(true);
          if (suggestions.length > 0) {
            const el = getInputEl();
            if (el) {
              el.dataset.suppressTranslate = "true";
              el.classList.add("suppress-translate");
            }
            document.dispatchEvent(new CustomEvent("dismiss-tamil-suggestions"));
          }
        }
      }}
      onInputCapture={() => {
        if (enabled) {
          setShowSuggestions(true);
        }
      }}
      onKeyDownCapture={handleKeyDownInternal}
    >
      {renderInput ? (
        renderInput({ field, setShowSuggestions, handleSelect })
      ) : (
        <input
          {...field}
          ref={inputRef}
          type="text"
          data-autocomplete="village"
          className={`${className || ""} village-input`}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          onChange={(e) => {
            field.onChange(e);
            if (enabled) {
              setShowSuggestions(true);
            }
          }}
          onFocus={() => {
            if (enabled) {
              setShowSuggestions(true);
              if (suggestions.length > 0) {
                const el = getInputEl();
                if (el) {
                  el.dataset.suppressTranslate = "true";
                  el.classList.add("suppress-translate");
                }
                document.dispatchEvent(
                  new CustomEvent("dismiss-tamil-suggestions")
                );
              }
            }
          }}
        />
      )}
      {enabled && showSuggestions && suggestions.length > 0 && !disabled && (
        <ul className="absolute z-[99999] mt-1 max-h-60 w-full overflow-auto rounded-xl border border-gray-200 bg-white py-1 shadow-2xl ring-1 ring-black/5">
          {suggestions.map((suggestion, idx) => (
            <li
              key={idx}
              className={`cursor-pointer px-4 py-2.5 text-sm font-medium transition-colors ${
                highlightedIndex === idx
                  ? "bg-indigo-50 text-indigo-700 font-semibold"
                  : "text-gray-800 hover:bg-indigo-50 hover:text-indigo-700"
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(suggestion);
              }}
              onMouseEnter={() => setHighlightedIndex(idx)}
            >
              {suggestion}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default VillageAutocomplete;
